import React, { useState, useEffect, useRef } from 'react';
import { PostHogProvider, usePostHog } from '@posthog/react';
import { isEqual } from 'lodash';
import SessionStore from 'stores/SessionStore';
import {
  isProd,
  isAcceptance,
  isStaging,
  isTest,
} from 'helpers/helper_functions';

const apiKey = `${POSTHOG_API_KEY}`;
const posthogDisabled = `${POSTHOG_DISABLED}` === 'true';
const trackingEnabled = !isTest() && !posthogDisabled;

function getEnvironment() {
  if (isProd()) return 'production';
  if (isStaging()) return 'staging';
  if (isAcceptance()) return 'acceptance';
  return 'development';
}

const options = {
  api_host: `${PROXYMATIC_URL}/posthog`,
  ui_host: 'https://us.posthog.com',
  defaults: '2026-01-30',
  autocapture: trackingEnabled,
  disable_session_recording: !trackingEnabled,
  capture_pageview: trackingEnabled,
  loaded: (posthog) => {
    posthog.register({ environment: getEnvironment() });
  },
};

function PostHogIdentify({ user, children }) {
  const posthog = usePostHog();
  const prevUserIdRef = useRef(null);

  useEffect(() => {
    if (!posthog) return;

    const prevUserId = prevUserIdRef.current;
    const currentUserId = user?._id ?? null;

    if (!currentUserId) {
      if (prevUserId) {
        posthog.reset();
      }
      prevUserIdRef.current = null;
      return;
    }

    posthog.identify(currentUserId, {
      email: user.email,
      name: user.name || user.email,
      userType: user._type,
      roles: user.roles ?? [],
      tags: user.tags ?? [],
    });

    prevUserIdRef.current = currentUserId;
  }, [posthog, user]);

  return <>{children}</>;
}

const PostHogWrapper = ({ children }) => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const handleSessionChange = () => {
      const currentUser = SessionStore.getUser();
      setUser((prev) => (isEqual(prev, currentUser) ? prev : currentUser));
    };

    handleSessionChange();
    SessionStore.addChangeListener(handleSessionChange);
    return () => {
      SessionStore.removeChangeListener(handleSessionChange);
    };
  }, []);

  if (!apiKey) {
    console.log('[PH] No API key configured; skipping PostHog');
    return children;
  }

  if (posthogDisabled) {
    console.log('[PH] PostHog disabled for this environment');
    return children;
  }

  return (
    <PostHogProvider apiKey={apiKey} options={options}>
      <PostHogIdentify user={user}>{children}</PostHogIdentify>
    </PostHogProvider>
  );
};

export default PostHogWrapper;
